import { FC, useContext, useState } from "react";
import { Box, createStyles, makeStyles, Typography } from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import LockIcon from "@material-ui/icons/LockOutlined";

import { LogInRequiredModal } from "components";
import MarketplaceMedianModal from "components/MarketplaceMedianModal";
import { AppContext } from "contexts";
import {
  formatMarketplaceMedian,
  formatMarketplaceMedianAmountSource,
  formatNumber,
} from "lib/format";
import theme from "theme";

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      display: "flex",
      flexDirection: "column",
      [theme.breakpoints.down("xs")]: {
        textAlign: "center",
      },
    },
    title: {
      fontSize: "1.2rem",
      fontWeight: theme.typography.fontWeightRegular,
      display: "flex",
      [theme.breakpoints.down("xs")]: {
        justifyContent: "center",
      },
    },
    marketPlaceMedianAmount: {
      fontSize: "1.5rem",
      fontWeight: theme.typography.fontWeightRegular,
    },
    marketPlaceMedianMultiple: {
      fontSize: "0.9rem",
      fontWeight: theme.typography.fontWeightLight,
    },
    iconContainer: {
      display: "flex",
      alignItems: "center",
      marginLeft: theme.spacing(1),
    },
    showPrice: {
      fontWeight: theme.typography.fontWeightRegular,
      textDecoration: "underline",
      marginTop: theme.spacing(1),
    },
    hoverPointer: {
      "&:hover": {
        cursor: "pointer",
      },
    },
  }),
);

interface MarketplaceMedianProps {
  marketplaceMedian: string | null;
  marketplaceMedianMultiplier: number;
  marketplaceMedianSource: string;
}

const MarketplaceMedian: FC<MarketplaceMedianProps> = ({
  marketplaceMedian,
  marketplaceMedianMultiplier,
  marketplaceMedianSource,
}) => {
  const { user } = useContext(AppContext);
  const canSeeMarketplaceMedian = !!user;

  const classes = useStyles();

  const [isLoginRequiredModalOpen, setIsLoginRequiredModalOpen] = useState(
    false,
  );
  const [
    isMarketplaceMedianModalOpen,
    setIsMarketplaceMedianModalOpen,
  ] = useState(false);

  return (
    <>
      {isLoginRequiredModalOpen && (
        <LogInRequiredModal
          open
          onClose={() => setIsLoginRequiredModalOpen(false)}
        />
      )}
      {isMarketplaceMedianModalOpen && (
        <MarketplaceMedianModal
          open
          onClose={() => setIsMarketplaceMedianModalOpen(false)}
        />
      )}
      <Box className={classes.container}>
        <Typography variant="h6" className={classes.title}>
          Marketplace Median{" "}
          <span className={`${classes.iconContainer} ${classes.hoverPointer}`}>
            {canSeeMarketplaceMedian ? (
              <InfoOutlinedIcon
                onClick={() => {
                  setIsMarketplaceMedianModalOpen(true);
                }}
              />
            ) : (
              <LockIcon onClick={() => setIsLoginRequiredModalOpen(true)} />
            )}
          </span>
        </Typography>
        <Typography variant="body1" className={classes.marketPlaceMedianAmount}>
          {formatMarketplaceMedian(marketplaceMedian)}
        </Typography>
        <Typography
          variant="body2"
          className={classes.marketPlaceMedianMultiple}
        >
          {canSeeMarketplaceMedian && marketplaceMedian === null ? (
            <span> — </span>
          ) : canSeeMarketplaceMedian && !!marketplaceMedianMultiplier ? (
            <span>
              {formatNumber(marketplaceMedianMultiplier)} x{" "}
              {formatMarketplaceMedianAmountSource(marketplaceMedianSource)}
            </span>
          ) : (
            <span
              className={`${classes.showPrice} ${classes.hoverPointer}`}
              onClick={() => setIsLoginRequiredModalOpen(true)}
            >
              Show Price (Users Only)
            </span>
          )}
        </Typography>
      </Box>
    </>
  );
};

export default MarketplaceMedian;
